/**
 * AudioRecordingBar - WhatsApp-style inline audio recording controls
 *
 * This is a presentation-only component designed to be flexible and reusable
 * across different screens (CreateRequest, RequestDetail, etc.).
 *
 * Features:
 * - Slide-up/slide-down animation when shown/hidden
 * - WhatsApp-inspired horizontal layout
 * - Pause/Resume toggle
 * - Discard and Accept actions
 * - Timer display in MM:SS format
 *
 * Usage:
 * ```tsx
 * <AudioRecordingBar
 *   recordingState={recordingState}
 *   elapsedTime={elapsedTime}
 *   visible={recordingState !== 'idle'}
 *   onPause={handlePause}
 *   onResume={handleResume}
 *   onDiscard={handleDiscard}
 *   onAccept={handleAccept}
 * />
 * ```
 */

import React, { useEffect, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Animated,
  Platform,
  ViewStyle,
} from 'react-native';
import { Trash2, Pause, Mic, Check } from '../icons';

interface AudioRecordingBarProps {
  /** Current recording state */
  recordingState: 'idle' | 'recording' | 'paused';
  /** Elapsed time in centiseconds (1/100 second) */
  elapsedTime: number;
  /** Whether the bar is visible */
  visible: boolean;
  /** Callback when pause button is pressed (during recording) */
  onPause: () => void;
  /** Callback when resume button is pressed (during pause) */
  onResume: () => void;
  /** Callback when discard button is pressed */
  onDiscard: () => void;
  /** Callback when accept button is pressed */
  onAccept: () => void;
  /** Custom positioning style */
  style?: ViewStyle;
}

export const AudioRecordingBar: React.FC<AudioRecordingBarProps> = ({
  recordingState,
  elapsedTime,
  visible,
  onPause,
  onResume,
  onDiscard,
  onAccept,
  style,
}) => {
  const slideAnim = useRef(new Animated.Value(0)).current;

  // Animate slide up/down based on visibility
  useEffect(() => {
    Animated.spring(slideAnim, {
      toValue: visible ? 1 : 0,
      useNativeDriver: true,
      tension: 65,
      friction: 10,
    }).start();
  }, [visible, slideAnim]);

  // Format elapsed time as MM:SS (convert from centiseconds)
  const formatTime = (centiseconds: number): string => {
    const totalSeconds = Math.floor(centiseconds / 100);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Calculate translation for slide animation
  const translateY = slideAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [100, 0], // Slide from 100px below to 0
  });

  const opacity = slideAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
  });

  if (!visible && recordingState === 'idle') {
    return null;
  }

  return (
    <Animated.View
      style={[
        styles.container,
        {
          transform: [{ translateY }],
          opacity,
        },
        style,
      ]}
    >
      <View style={styles.content}>
        {/* Trash/Discard Button */}
        <TouchableOpacity
          testID="audio-recording-discard"
          style={styles.actionButton}
          onPress={onDiscard}
          activeOpacity={0.7}
        >
          <Trash2 size={20} color="#666" />
        </TouchableOpacity>

        {/* Timer Display */}
        <View style={styles.timerContainer}>
          <Text style={styles.timerText}>{formatTime(elapsedTime)}</Text>
        </View>

        {/* Pause/Resume Toggle Button */}
        <TouchableOpacity
          testID="audio-recording-pause-resume"
          style={[styles.toggleButton, recordingState === 'recording' && styles.toggleButtonRecording]}
          onPress={recordingState === 'recording' ? onPause : onResume}
          activeOpacity={0.7}
        >
          {recordingState === 'recording' ? (
            <Pause size={20} color="#fff" />
          ) : (
            <Mic size={20} color="#fff" />
          )}
        </TouchableOpacity>

        {/* Accept Button */}
        <TouchableOpacity
          testID="audio-recording-accept"
          style={styles.acceptButton}
          onPress={onAccept}
          activeOpacity={0.7}
        >
          <Check size={20} color="#fff" />
        </TouchableOpacity>
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: Platform.select({
      web: 60, // Above footer on web
      ios: 94, // Above footer + safe area on iOS
      android: 70, // Above footer on Android
    }),
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    ...Platform.select({
      web: {
        left: 'auto' as any,
        right: 0,
        marginLeft: 60, // Account for sidebar on web
      },
    }),
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: -2,
    },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 5,
    zIndex: 998, // Below modals but above most content
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 12,
    maxWidth: Platform.select({
      web: 1200,
      default: undefined,
    }),
    alignSelf: Platform.select({
      web: 'center' as any,
      default: 'stretch',
    }),
    width: '100%',
  },
  actionButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#f5f5f5',
    alignItems: 'center',
    justifyContent: 'center',
  },
  timerContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: 16,
  },
  timerText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    fontVariant: ['tabular-nums'],
  },
  toggleButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#666',
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: 8,
  },
  toggleButtonRecording: {
    backgroundColor: '#ff6b35',
  },
  acceptButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#28a745',
    alignItems: 'center',
    justifyContent: 'center',
  },
});
